import React, { useState , useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import GetIcon from "@/components/font-awesome-icon";
import UIButton from "@/components/ui-controls/button";
import { usePathname, useRouter } from "next/navigation";
import { faFileExport } from "@fortawesome/free-solid-svg-icons/faFileExport";
import { faFileImport } from "@fortawesome/free-solid-svg-icons/faFileImport";
import ManageColPopover from "./manage-col-popover";
import {  useDispatch } from "react-redux";
import UITextField from "../ui-controls/textfield";
import SearchOutlined from "@mui/icons-material/SearchOutlined";

import debounce from "lodash.debounce";
import GoBack from "@/components/go-back";
import { Menu, MenuItem } from "@mui/material";

import {
  setImportMode,
  setSelectedImportToState,
} from "@/store/slices/import/importSlice";
import trackingService from "@/services/tracking/tracking-service";
import { TRACKING_EVENTS } from "@/services/tracking/tracking-events";
import { clearLastVisitedRoute } from "@/store/slices/route-history/routeHistorySlice";
import { clearGridState } from "@/store/slices/grid/gridStateSlice";

const buttonVisibleOptions = {
  actions: false,
  CompanySwitch: false,
  manageColumns: false,
  import: false,
  export: false,
  onExport: null,
};

const PageHeader = ({
  title = "",
  buttons = [],
  columns = [],
  setColumns,
  handleClick,
  options = buttonVisibleOptions,
  disabledColumns = [],
  defaultColumns = [],
  customFields = [],
  variant = "h3",
  children = null,
  searchField = null,
  searchCallback = () => {},
  sx = {},
  titleBoxSx = {},
}) => {
  const [searchValue, setSearchValue] = useState("");
  const pathName = usePathname();
  const router = useRouter();
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();
  const openMenu = Boolean(anchorEl);

  const handleExportLead = () => {
    if (options?.onExport && typeof options?.onExport === "function") {
      options.onExport();
    }
  };

  const debouncedChangeHandler = useCallback(debounce(searchCallback, 500), []);


  const handleGoBack = () => {
    if (typeof options?.goBack?.onClick === "function") {
      options.goBack.onClick();
    }
    options?.goBack?.url ? router.push(options?.goBack?.url) : router.back();
    if (options?.goBack?.saveRouteKey) {
      dispatch(
        clearLastVisitedRoute({ routeKey: options?.goBack?.saveRouteKey })
      );
    }
    if (options?.goBack?.clearGridState) {
      options?.goBack?.clearGridState?.forEach((gridId) => {
        dispatch(clearGridState({ gridId }));
      });
    }
  };

  const { alignItems = "center", ...headerSx } = sx || {};

  return (
    <Box
      sx={{
        py: 2,
        marginTop: "2px",
        ...headerSx,
      }}
    >
      {options?.showGoBack && (
        <GoBack
          onClick={handleGoBack}
          title={options?.goBack?.text}
          sx={{ mb: 0.5 }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems,
        }}
      >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-start",
          alignItems: "center",
          ...titleBoxSx,
        }}
      >
        <>
          <Typography
            variant={variant}
            color={"var(--palette-heading)"}
          >
            {title}
          </Typography>
          {searchField && (
            <Box sx={{ width: { sm: "100%", md: "300px" }, ml: 2 }}>
              <UITextField
                name={"search"}
                size="small"
                value={searchValue}
                inputPropsStyles={{ paddingLeft: "6px" }}
                placeholder={searchField?.placeholder || "Search"}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  debouncedChangeHandler(e.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    { borderWidth: "1px" },
                }}
                prefix={<SearchOutlined color="primary" />}
              />
            </Box>
          )}
          {children && children}
        </>
      </Box>

      {/* buttons-section */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        {options?.manageColumns && (
          <ManageColPopover
            columns={columns}
            setColumns={setColumns}
            disabledColumns={disabledColumns}
            defaultColumns={defaultColumns}
            customFields={customFields}
          />
        )}

        {options?.import && (
          <>
            <UIButton
              key={2}
              className="tour-import-lead-btn"
              variant={"outlined"}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <GetIcon title="Import" icon={faFileImport} />
            </UIButton>

            <Menu
              anchorEl={anchorEl}
              id={"importMenu"}
              keepMounted
              open={openMenu}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                key={1}
                onClick={() => {
                  trackingService.track(TRACKING_EVENTS.IMPORT_LEAD);
                  setAnchorEl(null);
                  dispatch(setSelectedImportToState({}));
                  dispatch(setImportMode(2));
                  router.push(`${pathName}/import`);
                }}
              >
                Import Leads
              </MenuItem>
              <MenuItem
                key={2}
                onClick={() => {
                  setAnchorEl(null);
                  dispatch(setSelectedImportToState({}));
                  dispatch(setImportMode(1));
                  router.push(`${pathName}/import`);
                }}
              >
                Import History
              </MenuItem>
            </Menu>
          </>
        )}

        {options?.export && (
          <UIButton
            className="tour-export-lead-btn"
            // loading={options?.exportLoading}
            disabled={options?.exportLoading}
            key={3}
            variant={"outlined"}
            onClick={handleExportLead}
          >
            <GetIcon title="Export" icon={faFileExport} />
          </UIButton>
        )}

        {buttons.map((button, index) => {
          return <React.Fragment key={index}>{button}</React.Fragment>;
        })}
      </div>
      </Box>
    </Box>
  );
};

export default PageHeader;
